import React from 'react'
import { supabase } from '@/lib/supabase'
import { applyOptimisticReactionToggle } from '@/shared/utils/reactions'
import {
  fetchVisionCardReactions,
  toggleVisionCardReaction,
} from '../services/visionCommunityService'
import type { CommunityReaction } from '@/modules/comunidade/services/communityService'

type UseVisionCardReactionsResult = {
  loading: boolean
  reactions: CommunityReaction[]
  toggleEmoji: (emoji: string) => Promise<void>
  reload: () => Promise<void>
}

export function useVisionCardReactions(
  visionId: number | null,
  active: boolean,
): UseVisionCardReactionsResult {
  const [loading, setLoading] = React.useState(false)
  const [reactions, setReactions] = React.useState<CommunityReaction[]>([])

  const load = React.useCallback(async () => {
    if (!visionId || !active) {
      setReactions([])
      return
    }

    setLoading(true)
    try {
      const data = await fetchVisionCardReactions(visionId)
      setReactions(data)
    } catch {
      setReactions([])
    } finally {
      setLoading(false)
    }
  }, [active, visionId])

  React.useEffect(() => {
    if (!visionId || !active) {
      setReactions([])
      return
    }

    let alive = true
    let interval: any = null

    ;(async () => {
      await load()
      if (!alive) return
    })()

    const channel = supabase
      .channel(`vision_card:${visionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'vision_community_reactions',
          filter: `vision_id=eq.${visionId}`,
        } as any,
        () => {
          load().catch(() => {
            // ignorar: realtime/polling corrige
          })
        },
      )
      .subscribe()

    interval = setInterval(() => {
      load().catch(() => {
        // silencioso; proximo ciclo corrige
      })
    }, 3500)

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // noop
      }
      try {
        if (interval) clearInterval(interval)
      } catch {
        // noop
      }
      alive = false
    }
  }, [active, load, visionId])

  const toggleEmoji = React.useCallback(
    async (emoji: string) => {
      if (!visionId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      setReactions((prev) => applyOptimisticReactionToggle(prev, trimmed))

      void toggleVisionCardReaction(visionId, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[Vision] toggleVisionCardReaction error', error)
        load().catch(() => {
          // silencioso; proximo ciclo/realtime corrige
        })
      })
    },
    [active, visionId, load],
  )

  return {
    loading,
    reactions,
    toggleEmoji,
    reload: load,
  }
}
